// src/components/CsvToJsonConverter.js
import React, { useState, useRef, useEffect } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import {
  FaUpload,
  FaCopy,
  FaFileExport,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
} from "react-icons/fa";
import { ClipLoader } from "react-spinners";

const CsvToJsonConverter = () => {
  // ----------------- Basic States -----------------
  const [csvInput, setCsvInput] = useState("");
  const [jsonOutput, setJsonOutput] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // ----------------- Line Numbers -----------------
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [lineNumbers, setLineNumbers] = useState([]);

  // ----------------- Advanced Options (hidden by default) -----------------
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 1) Root Key
  const [rootKey, setRootKey] = useState("");
  // 2) Separator
  const [separator, setSeparator] = useState(","); // Comma default
  // 3) Trim Quotes
  const [trimQuotes, setTrimQuotes] = useState(false);
  // 4) Ignore Empty Lines
  const [ignoreEmptyLines, setIgnoreEmptyLines] = useState(false);
  // 5) Ignore Empty Cells
  const [ignoreEmptyCells, setIgnoreEmptyCells] = useState(false);
  // 6) Fix CSV Mismatch (in memory parse)
  const [fixCsv, setFixCsv] = useState(false);

  // 7) Dark Mode
  const [darkMode, setDarkMode] = useState(false);

  // Refs
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const outputRef = useRef(null);

  // Example CSV
  const exampleCsv = `name,age,city
Alice,30,New York
Bob,25,Los Angeles`;

  // ----------------- Effects -----------------
  // Update line numbers on text or toggle
  useEffect(() => {
    if (showLineNumbers) {
      setLineNumbers(csvInput.split("\n").map((_, i) => i + 1));
    }
  }, [csvInput, showLineNumbers]);

  // Dark Mode
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // ----------------- CSV Parsing Logic -----------------
  /**
   * parseSingleCsv: parse one CSV text block (no "=====").
   * If fixCsv is true, we fill/truncate mismatched rows.
   * Else, skip them.
   */
  const parseSingleCsv = (csvData) => {
    let rows = csvData.split("\n");

    if (ignoreEmptyLines) {
      rows = rows.filter((row) => row.trim().length > 0);
    }
    if (rows.length === 0) {
      return rootKey.trim() ? { [rootKey.trim()]: [] } : [];
    }

    // parse header
    const headers = rows[0].split(separator).map((h) => h.trim());
    const expectedCols = headers.length;

    // parse data rows
    const records = rows.slice(1).map((row) => {
      let values = row.split(separator);

      if (values.length !== expectedCols) {
        if (fixCsv) {
          // fix row
          if (values.length < expectedCols) {
            // fill missing
            while (values.length < expectedCols) {
              values.push("");
            }
          } else {
            // truncate extras
            values = values.slice(0, expectedCols);
          }
        } else {
          // skip row
          return null;
        }
      }

      const record = {};
      headers.forEach((header, colIndex) => {
        let rawVal = values[colIndex] || "";
        if (trimQuotes) {
          rawVal = rawVal.replace(/^"(.*)"$/, "$1");
        }
        rawVal = rawVal.trim();
        if (ignoreEmptyCells && rawVal === "") {
          // skip
        } else {
          record[header] = rawVal;
        }
      });
      return record;
    });

    const finalRecords = records.filter((r) => r !== null);

    // wrap in rootKey if provided
    if (rootKey.trim()) {
      return { [rootKey.trim()]: finalRecords };
    } else {
      return finalRecords;
    }
  };

  /**
   * handleConvert
   * If input includes "=====", parse each chunk as separate CSV,
   * else parse as a single CSV. Combine results in JSON output.
   */
  const handleConvert = () => {
    try {
      setProcessing(true);
      setTimeout(() => {
        if (!csvInput.trim()) {
          setJsonOutput("");
          setError("No CSV data provided.");
          setProcessing(false);
          return;
        }

        const delimiter = "=====";
        if (csvInput.includes(delimiter)) {
          // parse multiple CSV chunks
          const chunks = csvInput.split(delimiter);
          const allResults = [];
          for (const chunk of chunks) {
            const trimmed = chunk.trim();
            if (!trimmed) {
              allResults.push("(Empty CSV chunk)");
            } else {
              const parsed = parseSingleCsv(trimmed);
              allResults.push(parsed);
            }
          }
          setJsonOutput(JSON.stringify(allResults, null, 2));
          setError("");
        } else {
          // single CSV
          const parsed = parseSingleCsv(csvInput);
          setJsonOutput(JSON.stringify(parsed, null, 2));
          setError("");
        }

        setCopySuccess(false);
        setProcessing(false);
      }, 700);
    } catch (err) {
      setJsonOutput("");
      setError(`Error: ${err.message}`);
      setCopySuccess(false);
      setProcessing(false);
    }
  };

  // ----------------- "Fix CSV" In Place (physically rewriting lines) -----------------
  /**
   * handleFixCsvInPlace
   * Goes through the text area data chunk by chunk, tries to fix mismatch columns
   * by physically rewriting lines. Also applies trimQuotes, ignoreEmptyLines.
   * (Ignores "ignoreEmptyCells" in place to avoid dropping columns.)
   */
  const handleFixCsvInPlace = () => {
    if (!csvInput.trim()) {
      setError("No CSV data provided.");
      return;
    }

    setProcessing(true);
    setTimeout(() => {
      try {
        const delimiter = "=====";
        const chunks = csvInput.split(delimiter);
        const outChunks = [];

        for (let chunk of chunks) {
          let lines = chunk.trim().split("\n");
          if (ignoreEmptyLines) {
            lines = lines.filter((line) => line.trim().length > 0);
          }
          if (lines.length === 0) {
            outChunks.push("(empty CSV chunk after fix)");
            continue;
          }

          // parse header as columns
          let headerFields = lines[0].split(separator);
          if (trimQuotes) {
            headerFields = headerFields.map((f) => f.replace(/^"(.*)"$/, "$1").trim());
          }
          const expectedCols = headerFields.length;

          // rewrite lines
          const newLines = [];
          // rewrite header line
          newLines.push(headerFields.join(separator));

          // data lines
          for (let i = 1; i < lines.length; i++) {
            let row = lines[i].split(separator);
            // fix mismatch
            if (row.length < expectedCols) {
              // fill
              while (row.length < expectedCols) {
                row.push("");
              }
            } else if (row.length > expectedCols) {
              row = row.slice(0, expectedCols);
            }
            // trim quotes
            if (trimQuotes) {
              row = row.map((cell) => cell.replace(/^"(.*)"$/, "$1").trim());
            }
            // rejoin
            newLines.push(row.join(separator));
          }

          const fixedChunk = newLines.join("\n");
          outChunks.push(fixedChunk);
        }

        // rejoin all chunks with delimiter
        const finalText = outChunks.join(`\n${delimiter}\n`);
        setCsvInput(finalText);

        // re-build line numbers
        setLineNumbers(finalText.split("\n").map((_, i) => i + 1));
        setError("");
      } catch (err) {
        setError(`Error fixing CSV: ${err.message}`);
      }
      setProcessing(false);
    }, 700);
  };

  // ----------------- Basic Actions -----------------
  const clearInputs = () => {
    setCsvInput("");
    setJsonOutput("");
    setError("");
    setCopySuccess(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonOutput);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleExport = () => {
    const blob = new Blob([jsonOutput], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "output.json";
    link.click();
  };

  const handleValidateJson = () => {
    if (!jsonOutput) {
      alert("No JSON to validate!");
      return;
    }
    try {
      JSON.parse(jsonOutput);
      alert("JSON is valid!");
    } catch (err) {
      alert("Invalid JSON:\n" + err.message);
    }
  };

  const handlePreviewInNewTab = () => {
    if (!jsonOutput) {
      alert("No JSON to preview!");
      return;
    }
    const newWin = window.open("", "_blank", "width=600,height=600");
    newWin.document.write(
      `<pre style="white-space:pre; font-family:monospace;">${jsonOutput
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>`
    );
    newWin.document.close();
  };

  // Upload CSV
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10 MB limit.");
      return;
    }

    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;

      // if text area is not empty, append
      if (csvInput.trim()) {
        const newContent = csvInput.trim() + `\n=====\n` + content;
        setCsvInput(newContent);
        setLineNumbers(
          (csvInput.trim() + `\n=====\n` + content)
            .split("\n")
            .map((_, i) => i + 1)
        );
      } else {
        // replace
        setCsvInput(content);
        setLineNumbers(content.split("\n").map((_, i) => i + 1));
      }

      setProcessing(false);
      setError("");
    };
    reader.readAsText(file);
  };

  // scroll helpers
  const scrollToTop = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      textareaRef.current.scrollTop = 0;
      lineNumbersRef.current.scrollTop = 0;
    }
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  };

  const scrollToBottom = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      lineNumbersRef.current.scrollTop = lineNumbersRef.current.scrollHeight;
    }
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  };

  // Renders the text area with line numbers
  const renderInputWithLineNumbers = (csv, setCsv) => (
    <ScrollSync>
      <div style={{ display: "flex", position: "relative", height: "300px" }}>
        {showLineNumbers && (
          <ScrollSyncPane>
            <div
              ref={lineNumbersRef}
              style={{
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
                borderRight: "1px solid #ccc",
                textAlign: "right",
                userSelect: "none",
                color: "#6c757d",
                padding: "5px",
                width: "40px",
                boxSizing: "border-box",
                height: "100%",
              }}
            >
              {lineNumbers.map((number) => (
                <div key={number} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                  {number}
                </div>
              ))}
            </div>
          </ScrollSyncPane>
        )}
        <ScrollSyncPane>
          <textarea
            ref={textareaRef}
            className="form-control"
            style={{
              flex: "1",
              height: "100%",
              resize: "none",
              border: error ? "2px solid red" : "2px solid #ccc",
              borderRadius: "0",
              overflowY: "auto",
              paddingLeft: showLineNumbers ? "0.5em" : "1em",
              fontFamily: "monospace",
            }}
            value={csv}
            onScroll={(e) => {
              if (lineNumbersRef.current) {
                lineNumbersRef.current.scrollTop = e.target.scrollTop;
              }
            }}
            onChange={(e) => {
              setCsv(e.target.value);
              setShowLineNumbers(true);
            }}
            placeholder={`Enter or upload CSV data here.\nIf you upload multiple files, they are separated by "=====".\nClick "Convert" to parse. If you want to physically fix columns, click "Fix CSV (In Place)" in Advanced.`}
          />
        </ScrollSyncPane>
        {/* Single "upload CSV" icon */}
        <label
          htmlFor="upload-file"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} />
        </label>
        <input
          type="file"
          id="upload-file"
          style={{ display: "none" }}
          accept=".csv"
          onChange={handleFileUpload}
        />
      </div>
    </ScrollSync>
  );

  // ----------------- UI Layout -----------------
  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button className="btn btn-sm btn-secondary" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      <div className="text-center mb-3">
        {processing && <ClipLoader color="#007bff" size={50} />}
        {error && <div className="text-danger mb-2">{error}</div>}

        <button
          className="btn btn-info"
          onClick={() => {
            setCsvInput(exampleCsv);
            setLineNumbers(exampleCsv.split("\n").map((_, i) => i + 1));
          }}
        >
          Use Example Data
        </button>
      </div>

      {/* CSV Input Area with line numbers */}
      <div className="row">
        <div className="col-md-12 mb-3">
          {renderInputWithLineNumbers(csvInput, setCsvInput)}
        </div>
      </div>

      {/* Convert / Clear / LN / Show Advanced Toggle */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={handleConvert} disabled={processing}>
          {processing ? "Converting..." : "Convert"}
        </button>
        <button className="btn btn-secondary me-2" onClick={clearInputs}>
          Clear
        </button>
        <label style={{ marginRight: "15px" }}>
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
          />{" "}
          Show Line Numbers
        </label>

        {/* Toggle advanced panel */}
        <button
          className="btn btn-outline-primary"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide Advanced" : "Show Advanced"}
        </button>
      </div>

      {/* Advanced Options Panel */}
      {showAdvanced && (
        <div
          style={{
            marginBottom: "10px",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
          }}
        >
          <div style={{ marginBottom: "8px" }}>
            <label style={{ marginRight: "5px" }}>
              Root Key:{" "}
              <input
                type="text"
                value={rootKey}
                onChange={(e) => setRootKey(e.target.value)}
                style={{ width: "120px", marginRight: "10px" }}
              />
            </label>

            <label style={{ marginRight: "10px" }}>
              Separator:
              <select
                style={{ marginLeft: "5px" }}
                value={separator}
                onChange={(e) => setSeparator(e.target.value)}
              >
                <option value=",">Comma (,)</option>
                <option value=";">Semicolon (;)</option>
                <option value="\t">Tab</option>
              </select>
            </label>

            <label style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                checked={trimQuotes}
                onChange={(e) => setTrimQuotes(e.target.checked)}
              />{" "}
              Trim Quotes
            </label>
            <label style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                checked={ignoreEmptyLines}
                onChange={(e) => setIgnoreEmptyLines(e.target.checked)}
              />{" "}
              Ignore Empty Lines
            </label>
            <label style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                checked={ignoreEmptyCells}
                onChange={(e) => setIgnoreEmptyCells(e.target.checked)}
              />{" "}
              Ignore Empty Cells
            </label>
            <label style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                checked={fixCsv}
                onChange={(e) => setFixCsv(e.target.checked)}
              />{" "}
              Fix Mismatched Rows (In Memory)
            </label>
          </div>

          {/* In-place fix button */}
          <div style={{ marginTop: "8px" }}>
            <button className="btn btn-sm btn-primary" onClick={handleFixCsvInPlace}>
              Fix CSV (In Place)
            </button>
            <small style={{ marginLeft: "8px", color: darkMode ? "#ccc" : "#666" }}>
              Rewrites the text area lines, filling/truncating columns, removing empty lines (if
              checked), and trimming quotes if selected.
            </small>
          </div>
        </div>
      )}

      {/* JSON Output */}
      {jsonOutput && (
        <div className="mt-4">
          <h5>Converted JSON</h5>
          <div
            ref={outputRef}
            style={{
              position: "relative",
              height: "300px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
              }}
            >
              {jsonOutput}
            </pre>
          </div>
          <div className="text-center mt-2">
            <button className="btn btn-secondary me-2" onClick={scrollToTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary me-2" onClick={scrollToBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"} me-2`}
              onClick={handleCopy}
            >
              <FaCopy /> {copySuccess ? "Copied!" : "Copy Output"}
            </button>
            <button className="btn btn-warning ms-2" onClick={handleExport}>
              <FaFileExport /> Export JSON
            </button>
            <button className="btn btn-info ms-2" onClick={handleValidateJson}>
              Validate JSON
            </button>
            <button className="btn btn-info ms-2" onClick={handlePreviewInNewTab}>
              Preview in New Tab
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvToJsonConverter;